/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * Hook Reutilizável de Avaliação Comercial e Entitlement (BLOCO 3)
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { UserProfile, CourtItem } from "../types/userProfile";
import { resolveCanonicalArenaId } from "../services/vendas/arenaIdentityResolver";
import {
  EntitlementService,
  CommercialEntitlementResult,
} from "../services/commercial/EntitlementService";
import { auth, db } from "../utils/firebase";
import { doc, getDoc, onSnapshot } from "firebase/firestore";

interface UseCommercialEntitlementOptions {
  userProfile?: UserProfile | null;
  courts?: CourtItem[] | null;
  now?: number; // Para testes
}

export function useCommercialEntitlement({
  userProfile,
  courts = [],
  now,
}: UseCommercialEntitlementOptions) {
  const currentUid = auth?.currentUser?.uid || userProfile?.id || "";
  const ownerUid = currentUid && !currentUid.startsWith("guest") ? currentUid : "";

  // 1. Resolve arenaId canônico do proprietário
  const canonicalArenaId = useMemo(() => {
    return resolveCanonicalArenaId({
      userProfile,
      courts: courts || [],
      ownerId: ownerUid,
    });
  }, [userProfile, courts, ownerUid]);

  // 2. Estado local da Arena
  const [arenaData, setArenaData] = useState<any>(() => {
    try {
      const saved = localStorage.getItem("app_arenas_canonical_v1");
      if (saved && canonicalArenaId) {
        const list = JSON.parse(saved);
        if (Array.isArray(list)) {
          return list.find((a: any) => a.id === canonicalArenaId || a.arenaId === canonicalArenaId) || null;
        }
      }
    } catch {}
    return null;
  });

  // 3. Listener em tempo real da Arena no Firestore (Apenas para perfis de donos/operadores/admins de arena)
  useEffect(() => {
    const role = String(userProfile?.role || userProfile?.accountType || "").toLowerCase();
    const isAthleteOrGuest =
      !ownerUid ||
      ownerUid.startsWith("guest") ||
      role === "atleta" ||
      role === "athlete" ||
      role === "user" ||
      role === "visitante";

    if (!canonicalArenaId || !db || isAthleteOrGuest) return;

    try {
      const unsub = onSnapshot(
        doc(db, "arenas", canonicalArenaId),
        (docSnap) => {
          if (docSnap.exists()) {
            const data = { id: docSnap.id, ...docSnap.data() };
            setArenaData(data);
          }
        },
        (err) => {
          console.warn("Aviso ao escutar documento da Arena para entitlement:", err);
        }
      );
      return () => unsub();
    } catch (e) {
      console.warn("Falha no listener da arena para entitlement:", e);
    }
  }, [canonicalArenaId, ownerUid, userProfile?.role, userProfile?.accountType]);

  // 4. Contagem canônica de quadras da Arena
  const arenaCourtsCount = useMemo(() => {
    if (!courts || !Array.isArray(courts) || courts.length === 0) return 0;
    if (!canonicalArenaId && !ownerUid) return 0;

    return courts.filter((c) => {
      const matchArena = canonicalArenaId && c.arenaId === canonicalArenaId;
      const matchOwner =
        ownerUid &&
        (c.ownerId === ownerUid ||
          c.userId === ownerUid ||
          (c as any).arenaOwnerId === ownerUid ||
          (c as any).ownerUid === ownerUid);
      return Boolean(matchArena || matchOwner);
    }).length;
  }, [courts, canonicalArenaId, ownerUid]);

  // 5. Avaliação do Entitlement
  const entitlement: CommercialEntitlementResult = useMemo(() => {
    return EntitlementService.getCommercialEntitlementStatus({
      arena: arenaData,
      userProfile,
      courtsCount: arenaCourtsCount,
      now,
    });
  }, [arenaData, userProfile, arenaCourtsCount, now]);

  // 6. Auto-inicialização do Trial se for Arena sem quadras
  useEffect(() => {
    if (
      userProfile?.accountType === "dono_arena" &&
      canonicalArenaId &&
      arenaCourtsCount === 0 &&
      !arenaData?.trialStartAt
    ) {
      EntitlementService.ensureArenaTrialInitialized(canonicalArenaId, arenaData, userProfile).catch(
        () => {}
      );
    }
  }, [userProfile?.accountType, canonicalArenaId, arenaCourtsCount, arenaData?.trialStartAt]);

  const canAccessOperational = entitlement.canAccessOperational;
  const isBlocked = !canAccessOperational;

  const canAccess = useCallback(
    (module: string) => {
      return entitlement.canAccess(module);
    },
    [entitlement]
  );

  return {
    entitlement,
    canAccessOperational,
    canAccessSales: entitlement.canAccessSales,
    canAccessFinance: entitlement.canAccessFinance,
    canAccessPDV: entitlement.canAccessPDV,
    canAccessStock: entitlement.canAccessStock,
    canAccessReports: entitlement.canAccessReports,
    canAccessTournament: entitlement.canAccessTournament,
    canAccessArenaConfig: entitlement.canAccessArenaConfig,
    canAccessCourtsConfig: entitlement.canAccessCourtsConfig,
    canAccessLogin: entitlement.canAccessLogin,
    canAccessProfile: entitlement.canAccessProfile,
    canAccess,
    isBlocked,
    canonicalArenaId,
    arenaData,
    arenaCourtsCount,
  };
}
